import { tmp } from "./tmpIds";
import type { DraftLine } from "./useLineDraftState";
import type { TmpId } from "@opus-dt/shared-types";

const SPACING_X = 200;
const Y = 200;

export function autoLayoutDraft(draft: DraftLine): DraftLine {
  const machines = Object.values(draft.machines);

  const existingStart = draft.canvas.nodes.find((n) => n.type === "START");
  const existingEnd = draft.canvas.nodes.find((n) => n.type === "END");

  const startTmp = (existingStart?.tmpId as TmpId) ?? tmp.node();
  const endTmp = (existingEnd?.tmpId as TmpId) ?? tmp.node();

  const machineNodeByMachine = new Map<string, TmpId>();
  for (const m of machines) {
    const existing = draft.canvas.nodes.find(
      (n) => n.type === "MACHINE" && n.machineTmpId === m.tmpId
    );
    machineNodeByMachine.set(m.tmpId, (existing?.tmpId as TmpId) ?? tmp.node());
  }

  const nodes: DraftLine["canvas"]["nodes"] = [];
  nodes.push({
    tmpId: startTmp,
    type: "START",
    label: existingStart?.label ?? "Início",
    x: existingStart?.x ?? 0,
    y: existingStart?.y ?? Y,
    machineTmpId: null,
    data: existingStart?.data ?? {},
  });
  machines.forEach((m, i) => {
    const machineNodeTmp = machineNodeByMachine.get(m.tmpId)!;
    const existingMachine = draft.canvas.nodes.find(
      (n) => n.type === "MACHINE" && n.machineTmpId === m.tmpId
    );
    nodes.push({
      tmpId: machineNodeTmp,
      type: "MACHINE",
      label: m.basic.name || m.basic.code || `Máquina ${i + 1}`,
      x: existingMachine?.x ?? SPACING_X * (i + 1),
      y: existingMachine?.y ?? Y,
      machineTmpId: m.tmpId,
      data: existingMachine?.data ?? {},
    });
  });
  nodes.push({
    tmpId: endTmp,
    type: "END",
    label: existingEnd?.label ?? "Fim",
    x: existingEnd?.x ?? SPACING_X * (machines.length + 1),
    y: existingEnd?.y ?? Y,
    machineTmpId: null,
    data: existingEnd?.data ?? {},
  });

  const edges: DraftLine["canvas"]["edges"] =
    draft.canvas.edges.length > 0
      ? draft.canvas.edges
      : autoGenLinearEdges(startTmp, endTmp, machines, machineNodeByMachine);

  return {
    ...draft,
    canvas: { nodes, edges },
  };
}

function autoGenLinearEdges(
  startTmp: TmpId,
  endTmp: TmpId,
  machines: DraftLine["machines"][string][],
  machineNodeByMachine: Map<string, TmpId>
): DraftLine["canvas"]["edges"] {
  const edges: DraftLine["canvas"]["edges"] = [];
  if (machines.length > 0) {
    let prev: TmpId = startTmp;
    for (const m of machines) {
      const machineNodeTmp = machineNodeByMachine.get(m.tmpId)!;
      edges.push({
        tmpId: tmp.edge(),
        sourceNodeTmpId: prev,
        targetNodeTmpId: machineNodeTmp,
        type: "PRODUCTION_FLOW",
        data: {},
      });
      prev = machineNodeTmp;
    }
    edges.push({
      tmpId: tmp.edge(),
      sourceNodeTmpId: prev,
      targetNodeTmpId: endTmp,
      type: "PRODUCTION_FLOW",
      data: {},
    });
  } else {
    edges.push({
      tmpId: tmp.edge(),
      sourceNodeTmpId: startTmp,
      targetNodeTmpId: endTmp,
      type: "PRODUCTION_FLOW",
      data: {},
    });
  }
  return edges;
}
